import App from './application-container';
import * as THREE from 'three';
import { LOG, display_message_to_user } from '@phi/logging/frontend';
import { ExtraData, Element as ExtraDataElement } from './ExtraData';
import { hide_shadows, show_shadows } from './colours_materials_appearance';
import { getImage, listDocumentElements } from '@phi/frontend/api/calls';
import { show_sketches, non_deleted_reference_sketches, number_of_non_deleted_reference_sketches } from './reference_sketches';
import { withSpinner } from './state/spinner';
import { loadRefSTLOnshape, request_partstudio_body_details } from './input_output';
import { uil_set_display_face_triangulation_res } from './ui_logic';
import { complete_setting_mirror_plane, complete_setting_symmetry_plane, view_all } from './scene';
import { find_camera_target_point_from_normalized_2d_screen_coordinates, get_camera_look_vector } from './utility';
import { uid_deselect_all_objects, uid_select_all_objects } from './ui_display';
import { StoredViewport } from '../../../../src/features/save-viewpoint';
import { clipping_planes } from '../../../../src/features/clipping';
import { frontend_service_container } from '@phi/frontend/service-container';

export function create_extra_data_string() {
  var extra_data = new ExtraData("v0.2"); // New version with separate storage of partstudio and stl reference models

  // Shadow and axes state
  var appearance_data_element = new ExtraDataElement("appearance");
  appearance_data_element.add_field("shadow", App.state.shadows_visible);
  appearance_data_element.add_field("axes", App.state.axes_visible);
  extra_data.add_data_element(appearance_data_element);

  // Educational license state
  var edu_data_element = new ExtraDataElement("edu");
  console_log("user_has_edu_license: ", App.state.edu);
  console_log("Saved file_has_edu_license: ", file_has_edu_license);
  edu_data_element.add_field("edu", App.state.edu || file_has_edu_license);
  extra_data.add_data_element(edu_data_element);

  // Viewports
  var viewports_element = new ExtraDataElement("viewports");
  App.state.stored_viewports.forEach(function(v) {
    viewports_element.add_field(v.name, v);
  });
  // Add one more viewport for the current one
  var v = new StoredViewport(
    uid_camera.left,
    uid_camera.right,
    uid_camera.top,
    uid_camera.bottom,
    viewSize,
    uid_camera.position,
    uid_camera.up,
    get_camera_look_vector(uid_camera),
    find_camera_target_point_from_normalized_2d_screen_coordinates(uid_camera, new THREE.Vector2(0,0)),
    "current_viewport");
  viewports_element.add_field("current_viewport", v);
  extra_data.add_data_element(viewports_element);

  // Imported Onshape reference models
  var imported_partstudio_reference_models_element = new ExtraDataElement("imported_partstudio_reference_models");
  imported_partstudio_reference_models_element.add_field("imported_partstudio_reference_models", App.state.imported_partstudio_reference_models);
  extra_data.add_data_element(imported_partstudio_reference_models_element);

  // Imported STL files
  var imported_stl_elements = new ExtraDataElement("imported_stl_models");

  // Here we need to work differently. When we load a reference model from Onshape, or a reference STL file,
  // we create one or more reference bodies in uid_reference_bodies. Subsequently the user can delete on or
  // more of these reference bodies. We should keep track of which ones are deleted (we do, in uid_reference_bodies)
  // and which reference model each one belongs to (we don't currently) in order to see which reference files we should
  // load and which not. Additionally we should remove the corresponding bodies from each. And also reposition them etc.

  // Instead, for now we only check whether the list of reference bodies is empty, in which case we don't save anything in
  // extra_data.
  //if (uid_reference_bodies.length == 0)
  //  App.state.imported_stl_models = [];
  imported_stl_elements.add_field("imported_stl_models", App.state.imported_stl_models);
  extra_data.add_data_element(imported_stl_elements);

  // New sketches loaded independently

  if (number_of_non_deleted_reference_sketches() > 0) {
    console_log("Storing new sketch data");
    var loaded_sketches_element = new ExtraDataElement("loaded_sketches");

    // Copy the imported_sketches object to remove mesh and material
    // and also set undefined IDs to ""
    var ls_copy_str = JSON.stringify(non_deleted_reference_sketches());
    var ls_copy = JSON.parse(ls_copy_str);
    ls_copy.forEach(function(el){
      el.mesh = null;
      el.material = null;
      if (!el.versionId)
        el.versionId = "";
      if (!el.workspaceId)
        el.workspaceId = "";
    });
    var jsonStr = JSON.stringify(ls_copy);
    console_log(jsonStr);

    loaded_sketches_element.add_field("string", jsonStr);
    extra_data.add_data_element(loaded_sketches_element);
  }

  // Symmetries
  if (App.state.symmetry_plane_selected) {
    var symmetries_element = new ExtraDataElement("symmetries");
    symmetries_element.add_field("symmetry_plane_base", symmetry_plane_base);
    symmetries_element.add_field("symmetry_plane_normal", symmetry_plane_normal);
    symmetries_element.add_field("symmetry_edges_list_first", symmetry_edges_list_first);
    symmetries_element.add_field("symmetry_edges_list_second", symmetry_edges_list_second);
    extra_data.add_data_element(symmetries_element);
  }

  // Mirror
  if (App.state.mirror_plane_selected) {
    var mirror_element = new ExtraDataElement("mirror");
    mirror_element.add_field("mirror_plane_base", mirror_plane_base);
    mirror_element.add_field("mirror_plane_normal", mirror_plane_normal);
    extra_data.add_data_element(mirror_element);
  }

  // Workspace units
  var units_data_element = new ExtraDataElement("units");
  units_data_element.add_field("units", App.state.display_units);
  extra_data.add_data_element(units_data_element);

  //Triangulation
  var triangulation_element = new ExtraDataElement("triangles");
  triangulation_element.add_field("triangles", uil_get_display_face_triangulation_res() );
  extra_data.add_data_element(triangulation_element);

  // section planes
  if (uid_state.clipping_has_been_set)
    extra_data.add_data_element(
      new ExtraDataElement('section_planes')
        .add_field('xymax', clipping_planes.xy.max)
        .add_field('yzmax', clipping_planes.yz.max)
        .add_field('xzmax', clipping_planes.xz.max)
        .add_field('xymin', clipping_planes.xy.min)
        .add_field('yzmin', clipping_planes.yz.min)
        .add_field('xzmin', clipping_planes.xz.min)
    );

  const str = JSON.stringify(extra_data);

  //Debugging
  console_log(str);
  const ed = JSON.parse(str);

  return(str);
}

export async function handle_extra_data(extra_data_string, is_default_model = false) {
  if (extra_data_string == "") {
    set_default_appearance();
    return;
  }

  var extra_data = JSON.parse(extra_data_string);
  if (!extra_data) {
    set_default_appearance();
    return;
  }

  var version = extra_data.version;
  var data = extra_data.data;
  //console.log(data);
  // Appearance
  if (typeof data.appearance !== 'undefined') {
    var d = data.appearance;
    // Shadow
    if (typeof d.shadow !== 'undefined' && d.shadow)
      show_shadows();
    else
      hide_shadows();
  }
  else {
    set_default_appearance();
  }

  // Educational license state
  if (typeof data.edu !== 'undefined') {
    var d = data.edu;
    file_has_edu_license = d.edu;
    if (file_has_edu_license)
      App.state.edu = true;
  } else {
    file_has_edu_license = false;
    //console.log("Loaded file has no edu data. ");
  }
  //console.log("Loaded file has edu license: ", file_has_edu_license);
  //console.log("file_has_edu_license: ", file_has_edu_license);

  // Viewport
  if (typeof data.viewports !== 'undefined') {
    var current_viewport_found = false;
    Object.keys(data.viewports).forEach(function(key) {
      var v = data.viewports[key];
      // Form data structure with vectors
      var position = new THREE.Vector3(v.position.x, v.position.y, v.position.z);
      var up = new THREE.Vector3(v.up.x, v.up.y, v.up.z);
      var look_vector = new THREE.Vector3(v.look_vector.x, v.look_vector.y, v.look_vector.z);
      var look_target = new THREE.Vector3(v.look_target.x, v.look_target.y, v.look_target.z);
      var vp = new StoredViewport(v.left, v.right, v.top, v.bottom, v.viewSize, position, up, look_vector, look_target, v.name);
      v = null;

      if (vp.name == "current_viewport") {
        current_viewport_found = true;
        move_to_viewport(vp);
      }
      else {
        App.state.stored_viewports.push(vp);
      }
    });
    if (!current_viewport_found)
      view_all();
  }
  else {
    view_all();
  }

  // Imported reference models
  // Handle v0.1 and v.02 extra_data differently
  await load_ref_models(version, data);
  await load_stl_models(data);

  // Symmetry
  if (typeof data.symmetries !== 'undefined') {
    App.state.symmetry_plane_selected = true;
    symmetry_plane_base.copy(data.symmetries.symmetry_plane_base);
    symmetry_plane_normal.copy(data.symmetries.symmetry_plane_normal);
    data.symmetries.symmetry_edges_list_first.forEach(function(eid){
      symmetry_edges_list_first.push(eid);
    });
    data.symmetries.symmetry_edges_list_second.forEach(function(eid){
      symmetry_edges_list_second.push(eid);
    });

    // Remove duplicates
    symmetry_edges_list_first = [...new Set(symmetry_edges_list_first)];
    symmetry_edges_list_second = [...new Set(symmetry_edges_list_second)];

    complete_setting_symmetry_plane();

    uid_select_all_objects();
    uid_deselect_all_objects();
  }

  // Mirror
  if (typeof data.mirror !== 'undefined') {
    App.state.mirror_plane_selected = true;
    mirror_plane_base.copy(data.mirror.mirror_plane_base);
    mirror_plane_normal.copy(data.mirror.mirror_plane_normal);
    complete_setting_mirror_plane();
  }

  if (is_default_model)
    uil_set_display_face_triangulation_res(App.state.triangles);
  else {
    // Workspace units
    if (typeof data.units !== 'undefined') {
      var u = data.units.units || "cm";
      App.state.display_units = u;
    }
  }

  // Reference sketches
  if (typeof data.loaded_sketches !== 'undefined') {
    var sketchData_array = JSON.parse(data.loaded_sketches.string);
    for (const loaded_sketch_element of sketchData_array) {
        if (loaded_sketch_element.workspaceId == "")
          loaded_sketch_element.workspaceId = null;
        if (loaded_sketch_element.versionId == "")
          loaded_sketch_element.versionId = null;

        var loader = new THREE.TextureLoader();
        loader.setCrossOrigin("");

        const img = await getImage(
            loaded_sketch_element.documentId,
            loaded_sketch_element.elementId,
            loaded_sketch_element.workspaceId,
            loaded_sketch_element.versionId,
        );

        loader.load(
            img,
            function(texture) {
                loaded_sketch_element.texture = texture;
                App.state.imported_sketches.push(loaded_sketch_element);
                attach_sketch_to_face(loaded_sketch_element);
                move_sketch_objects_out_of_objects_raynode();
                set_button_states();
                show_sketches();
            },
            () => {},
            (err) => {
                display_message_to_user(LOG.WARN, "Failed to load image");
                // Delete the relevant body
                uil_delete_body(loaded_sketch_element.body_id);
            });
    };
  }

  // section planes
  if (data.section_planes) {
    uid_state.clipping_has_been_set = true;
    clipping_planes.xy.max = data.section_planes.xymax;
    clipping_planes.yz.max = data.section_planes.yzmax;
    clipping_planes.xz.max = data.section_planes.xzmax;
    clipping_planes.xy.min = data.section_planes.xymin;
    clipping_planes.yz.min = data.section_planes.yzmin;
    clipping_planes.xz.min = data.section_planes.xzmin;
  }
}

export function validateLoadedSketchData(sketchData, version) {
  //XXX
}

// Function that validates correct form for sketchData
export function validateSketchData(sketchData, version) {

  // In case we're loading an old format (top, front, right, side) sketch data, transaalte it to the new
  // format (floor, left, right, front)

  if (version == "v0.0") {
    if (sketchData.rearDocumentId) {
      sketchData.rightDocumentId = sketchData.rearDocumentId;
      sketchData.rightElementId = sketchData.rearElementId;
      sketchData.rightWorkspaceId = sketchData.rearWorkspaceId;
      sketchData.right_filename = sketchData.rear_filename;
      sketchData.right_id = sketchData.rear_id;
      sketchData.right_position = sketchData.rear_position;
      sketchData.right_scale = sketchData.rear_scale;
      sketchData.right_flipped_h = sketchData.rear_flipped_h;
      sketchData.right_flipped_v = sketchData.rear_flipped_v;
    }
    else {
      sketchData.rightDocumentId = "";
      sketchData.rightElementId = "";
      sketchData.rightWorkspaceId = "";
      sketchData.right_filename = "";
      sketchData.right_id = "";
      sketchData.right_position = new THREE.Vector3(0,0,0);
      sketchData.right_scale = 1;
      sketchData.right_flipped_h = false;
      sketchData.right_flipped_v = false;
    }

    if (sketchData.topDocumentId) {
      sketchData.floorDocumentId = sketchData.topDocumentId;
      sketchData.floorElementId = sketchData.topElementId;
      sketchData.floorWorkspaceId = sketchData.topWorkspaceId;
      sketchData.floor_filename = sketchData.top_filename;
      sketchData.floor_id = sketchData.top_id;
      sketchData.floor_position = sketchData.top_position;
      sketchData.floor_scale = sketchData.top_scale;
      sketchData.floor_flipped_h = sketchData.top_flipped_h;
      sketchData.floor_flipped_v = sketchData.top_flipped_v;
    }
    else {
      sketchData.floorDocumentId = "";
      sketchData.floorElementId = "";
      sketchData.floorWorkspaceId = "";
      sketchData.floor_filename = "";
      sketchData.floor_id = "";
      sketchData.floor_position = new THREE.Vector3(0,0,0);
      sketchData.floor_scale = 1;
      sketchData.floor_flipped_h = false;
      sketchData.floor_flipped_v = false;
    }

    if (sketchData.frontDocumentId) {
      sketchData.leftDocumentId = sketchData.frontDocumentId;
      sketchData.leftElementId = sketchData.frontElementId;
      sketchData.leftWorkspaceId = sketchData.frontWorkspaceId;
      sketchData.left_filename = sketchData.front_filename;
      sketchData.left_id = sketchData.front_id;
      sketchData.left_position = sketchData.front_position;
      sketchData.left_scale = sketchData.front_scale;
      sketchData.left_flipped_h = sketchData.front_flipped_h;
      sketchData.left_flipped_v = sketchData.front_flipped_v;
    }
    else {
      sketchData.leftDocumentId = "";
      sketchData.leftElementId = "";
      sketchData.leftWorkspaceId = "";
      sketchData.left_filename = "";
      sketchData.left_id = "";
      sketchData.left_position = new THREE.Vector3(0,0,0);
      sketchData.left_scale = 1;
      sketchData.left_flipped_h = false;
      sketchData.left_flipped_v = false;
    }

    if (sketchData.sideDocumentId) {
      sketchData.frontDocumentId = sketchData.sideDocumentId;
      sketchData.frontElementId = sketchData.sideElementId;
      sketchData.frontWorkspaceId = sketchData.sideWorkspaceId;
      sketchData.front_filename = sketchData.side_filename;
      sketchData.front_id = sketchData.side_id;
      sketchData.front_position = sketchData.side_position;
      sketchData.front_scale = sketchData.side_scale;
      sketchData.front_flipped_h = sketchData.side_flipped_h;
      sketchData.front_flipped_v = sketchData.side_flipped_v;
    }
    else {
      sketchData.frontDocumentId = "";
      sketchData.frontElementId = "";
      sketchData.frontWorkspaceId = "";
      sketchData.front_filename = "";
      sketchData.front_id = "";
      sketchData.front_position = new THREE.Vector3(0,0,0);
      sketchData.front_scale = 1;
      sketchData.front_flipped_h = false;
      sketchData.front_flipped_v = false;
    }
  }

  var promises = []
  if(sketchData.leftDocumentId !== "") {
    if (sketchData.leftDocumentId !== onshape_document_id) {
      var pLeft = matchElementName(sketchData.left_filename).then((newElementId) => {
        sketchData.leftDocumentId = onshape_document_id;
        sketchData.leftElementId = newElementId;
        sketchData.leftWorkspaceId = onshape_workspace_id;
      }).catch(e => { throw e });
      promises.push(pLeft);
    }
  }

  if (sketchData.frontDocumentId !== "") {
    if (sketchData.frontDocumentId !== onshape_document_id) {
      var pFront = matchElementName(sketchData.front_filename).then((newElementId) => {
        sketchData.frontDocumentId = onshape_document_id;
        sketchData.frontElementId = newElementId;
        sketchData.frontWorkspaceId = onshape_workspace_id;
      }).catch(e => { throw e });
      promises.push(pFront);
    }
  }

  if (sketchData.rightDocumentId !== "") {
    if (sketchData.rightDocumentId !== onshape_document_id) {
      var pRight = matchElementName(sketchData.right_filename).then((newElementId) => {
        sketchData.rightDocumentId = onshape_document_id;
        sketchData.rightElementId = newElementId;
        sketchData.rightWorkspaceId = onshape_workspace_id;
      }).catch(e => { throw e });
      promises.push(pRight);
    }
  }

  if (sketchData.floorDocumentId !== "") {
    if (sketchData.floorDocumentId !== onshape_document_id) {
      var pFloor = matchElementName(sketchData.floor_filename).then((newElementId) => {
        sketchData.floorDocumentId = onshape_document_id;
        sketchData.floorElementId = newElementId;
        sketchData.floorWorkspaceId = onshape_workspace_id;
      }).catch ( e => {throw e});
      promises.push(pFloor);
    }
  }
  return Promise.all(promises);
}

export async function matchElementName(fileName) {
    const data = await listDocumentElements(App.state.document, App.state.workspace, false);
    if (data instanceof Error)
        throw new Error('could get document elements');
    const matching = data.filter(item => item.name == fileName);
    if (matching.length > 1)
        throw new Error('Error more than 1 matching file.');
    if (matching.length === 0)
        throw new Error('Error no matching elements.');
    const item = matching[0];
    if (item.id === null)
        throw new Error('Error missing id.');
    return item.id;
}

function is_valid_irm(irm) {
    return (
        irm.element_id !== null
        && irm.element_name !== null
        && irm.part_id !== null
        && irm.part_name !== null
        && irm.is_partstudio !== null
    );
}

async function load_ref_models(version, data) {
    const models = (() => { switch(version) {
        case 'v0.2':
            return data?.imported_partstudio_reference_models?.imported_partstudio_reference_models;

        case 'v0.1':
            return data?.imported_reference_models?.onshape_reference_models;
    }})();

    if (
        !frontend_service_container().authentication.session_type.value.has('onshape')
        && Array.isArray(models)
        && models.length > 0
    ) {
      return display_message_to_user(LOG.WARN, 'Reference models will not be loaded, as you are not currently authenticated with Onshape');
    }

    if (Array.isArray(models) && models.every(is_valid_irm))
        switch (version) {
            case 'v0.2':
                return await load_ref_models_v2(models);

            case 'v0.1':
                return await load_ref_models_v1(models);
        }
}

async function load_ref_models_v2(models) {
    if (models.length)
        display_message_to_user(
            LOG.INFO,
            models.length === 1
                ? 'Waiting to load one reference Onshape Part/PartStudio within this .phi file'
                : `Waiting to load ${models.length} reference Onshape Parts/PartStudios within this .phi file`
        );
    const loader = withSpinner('Importing reference models from Onshape', request_partstudio_body_details);
    for (const irm of models)
        await loader(
            irm.element_id,
            irm.element_name,
            irm.part_id,
            irm.part_name,
            true,
            irm.is_partstudio,
            true,
        );
}

async function load_ref_models_v1(models) {
    const loader = withSpinner('Importing reference models from Onshape', request_partstudio_body_details);
    for (const irm of models)
        await loader(
            irm.element_id,
            irm.element_name,
            irm.part_id,
            irm.part_name,
            true,
            irm.is_partstudio,
            true,
        );
}

async function load_stl_models(data) {
    const models = data?.imported_stl_models?.imported_stl_models;

    if (
        !frontend_service_container().authentication.session_type.value.has('onshape')
        && Array.isArray(models)
        && models.length > 0
    ) {
        return display_message_to_user(LOG.WARN, 'STL references will not be loaded, as you are not currently authenticated with Onshape');
    }

    const is_valid_irm = irm =>
        irm.elementId !== null
        && irm.documentId !== null
        && irm.units !== null
        && irm.isFlipped !== null;

    if (!Array.isArray(models))
        return;
    else if (!models.every(is_valid_irm))
        return display_message_to_user(LOG.DEBUG, 'Error importing STL models');
    else for (const irm of models) {
        await loadRefSTLOnshape(
            irm.elementId,
            irm.documentId,
            irm.workspaceId,
            irm.units,
            irm.isFlipped,
            false,
        );
    }
}
